import React, { ReactElement, useContext } from 'react'
import { ArtData } from '@/database/art'
import { shortenAddress } from '@/utils/shortenAddress'
import { crazyAmount, formatDateString, priceText, transferFeeText } from '@/utils/formatting'
import CountdownTimer from '@/components/auction/CountdownTimer'
import Button from '@/components/Button'
import { Context } from '@/components/Context'
import Box from '@/components/boxes/Box'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import VotesTooltip from '@/components/tooltips/VotesTooltip'
import ClaimsReceivedTooltip from '@/components/tooltips/ClaimsReceivedTooltip'
import ClaimsMadeTooltip from '@/components/tooltips/ClaimsMadeTooltip'
import { ArtState } from '@/database/types'

const AuctionCard = React.memo(
    ({
        artData,
        openModalAuctionCardDetails,
    }: {
        artData: ArtData
        openModalAuctionCardDetails: (artData: ArtData) => void
    }): ReactElement => {
        const { endAuction } = useContext(Context)

        return (
            <div className="header-container center">
                <div key={artData.art.id} className="artwork-box center width-hundred max-width-370 margin-box ">
                    <div className={'header-container gradient-text bold large-bold'}>
                        <Box caption={'FORCED AUCTION'} text={''} styleCaption={'gradient-text bold large-bold '} />
                    </div>
                    <div className="menu-wrapper p-copy">
                        <Button
                            btnName={`End Auction`}
                            classStyles="nav__button blue"
                            handleClick={() => artData.tokenId && endAuction(artData.tokenId)}
                            cancelBg={undefined}
                        />
                        <CountdownTimer auctionDate={artData.art.auctions?.[0]?.auctionEnd} />
                    </div>
                    <div className={'header-container'}>
                        <Box
                            caption={artData.tokenId ? 'NFT' : 'Art'}
                            text={'#' + (artData.tokenId ?? artData.art.id)}
                        />
                        <Box caption={'Title'} text={artData.art.title} />
                    </div>
                    <div className="header-container bottom">
                        {artData.state !== ArtState.FORCED_AUCTION && (
                            <Box
                                caption={'Sale Price'}
                                styleCaption={'gradient-text bold large-bold '}
                                text={priceText(artData, 'large-bold')}
                                textValue={artData?.art.price}
                                styleText={'center nav__voted-amount large'}
                                triggerAnimation={false}
                            />
                        )}
                        <Box
                            caption={'Highest Bid'}
                            text={artData.highestBid ?? crazyAmount(artData.highestBid, `white large-bold`)}
                            textValue={artData.highestBid ?? ' - '}
                            styleCaption={'gradient-text bold large-bold '}
                            styleText={'center nav__voted-amount large'}
                        />
                    </div>
                    <LoadImageButtonOnDemand artData={artData} onClick={() => openModalAuctionCardDetails(artData)} />
                    <div className={'header-container'}>
                        <Box caption={'Description'} text={artData.art.prompt} />
                    </div>
                    <div className={'header-container'}>
                        <Box caption={'Creator'} text={shortenAddress(artData.user.walletAddress)} styleText="small" />
                        <Box
                            caption={'Creation Date'}
                            text={formatDateString(artData.art.createdAt)}
                            styleText="small"
                        />
                    </div>
                    <VotesTooltip artData={artData} />
                    <ClaimsMadeTooltip artData={artData} />
                    <ClaimsReceivedTooltip artData={artData} />
                    <div className={'header-container'}>
                        <Box caption={'Claim Power'} text={artData.claimPower.toFixed(2)} />
                        <Box
                            caption={'Claims Made'}
                            id={'claims-made' + artData.art.id}
                            text={artData.totalClaims.toFixed(2)}
                        />
                        <Box
                            caption={'Claims Received'}
                            id={'claims-received' + artData.art.id}
                            text={artData.totalClaimed.toFixed(2)}
                        />
                    </div>
                    <div className={'header-container'}>
                        <Box
                            caption={'Transfer Fee'}
                            text={transferFeeText(artData)}
                            textValue={artData?.art.transferFee}
                        />
                    </div>
                    <Button
                        btnName={`Bid/Claim`}
                        classStyles="nav__medium"
                        handleClick={() => openModalAuctionCardDetails(artData)}
                        cancelBg={undefined}
                    />
                </div>
            </div>
        )
    },
)

AuctionCard.displayName = 'AuctionCard'

export default AuctionCard
